import { useCallback, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';
import { Button, Paper } from '@mui/material';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

// Utils
import { makeCompanyRelationColumns } from '../../../utils/columns/makeCompanyRelationColumn';
import { deleteCompanyRelation, getCompanyRelations } from '../../../services/companies.services';

// Types
import { AppPermissions } from '../../../constants/Permissions';
import { CompanyRelation } from '../../../types/Company';

// Common
import { useStandardTable as useTable, Table } from '../../common/table';
import { AuthGuard } from '../../common/AuthGuard';
import CustomModal from '../../common/withModal';

// Form
import CompanyRelationForm from './CompanyRelationForm';

interface CompaniesRelationViewProps {
  id: string;
}

const CompaniesRelationView = ({ id }: CompaniesRelationViewProps) => {
  const [companyRelationDetailsPopUp, setCompanyRelationDetailsPopUp] = useState(false);
  const [editingRelation, setEditingRelation] = useState<CompanyRelation | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Open modal for edit
  const handleEditCompanyRelation = (relation: CompanyRelation) => {
    setEditingRelation(relation);
    setCompanyRelationDetailsPopUp(true);
  };

  // Open modal for add
  const handleAddRelation = () => {
    setEditingRelation(null);
    setCompanyRelationDetailsPopUp(true);
  };

  // Delete company relation
  const handleDeleteCompanyRelation = async (relation: CompanyRelation) => {
    try {
      await deleteCompanyRelation(relation.companyId, relation.id);

      table.fetchData?.({});
    } catch (error) {
      console.error('Error deleting company relation:', error);
    }
  };

  const columns = useMemo(() => makeCompanyRelationColumns(handleEditCompanyRelation, handleDeleteCompanyRelation), []);

  const fetchRelations = useCallback(() => getCompanyRelations(id), [id]);

  const table = useTable<CompanyRelation>({
    columns,
    fetchAsync: fetchRelations,
    serverSide: true,
  });

  return (
    <AuthGuard permission={AppPermissions.Company.Read}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Company Relations</h3>
        <Button
          onClick={handleAddRelation}
          variant='contained'
          size='small'
          style={{ textTransform: 'none' }}
          endIcon={<AddCircleOutlineOutlinedIcon style={{ fontSize: 20 }} />}
        >
          Add Relation
        </Button>
      </div>
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={true} getCoreRowModel={getCoreRowModel()} />
      </Paper>
      <CustomModal
        title={editingRelation ? 'Update Company Relation Details' : 'Add Company Relation'}
        isOpen={companyRelationDetailsPopUp}
        setIsOpen={setCompanyRelationDetailsPopUp}
        showSaveButton={false}
        maxWidth='lg'
        customAction={() => null}
      >
        <CompanyRelationForm
          relation={editingRelation}
          companyId={id}
          onSuccess={() => {
            setCompanyRelationDetailsPopUp(false);
            table.fetchData?.({}); // refresh after add/update
          }}
          onCancel={() => setCompanyRelationDetailsPopUp(false)}
          onError={(msg) => setErrorMessage(msg)}
        />
        {errorMessage && <div style={{ color: 'red', marginTop: '1rem', textAlign: 'center' }}>{errorMessage}</div>}
      </CustomModal>
    </AuthGuard>
  );
};

export default CompaniesRelationView;
